import { useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSetAtom, useAtomValue } from 'jotai';
import { z } from 'zod';
import type { Recording } from '@minutes-system/backend/src/api/recordings/schema';
import { recordingsQueryParamsAtom, recordingsAtom, type RecordingsQueryParams } from '../states/state';
import buildNormalizedParams, { type RecordingFilters } from '../utils/buildNormalizedParams';
import convertJstDateToUtcIso from '../utils/convertJstDateToUtcIso';
import calculateMaxPage from '../../../../lib/calculateMaxPage';
import { PHONE_SEARCH_PATTERN } from '../../../../shared/phonePattern';

export type { RecordingFilters } from '../utils/buildNormalizedParams';

const PAGE_SIZE = 12;

// URLパラメータのzodスキーマ
const urlParamsSchema = z.object({
  page: z.coerce.number().int().positive().catch(1),
  'customer-name': z.string().min(1).optional().catch(undefined),
  // 部分一致検索のため 1〜11 桁の数字のみ許容（不正値は undefined に丸めて検索条件から除外）
  'customer-phone': z.string().regex(PHONE_SEARCH_PATTERN).optional().catch(undefined),
  'advisor-name': z.string().min(1).optional().catch(undefined),
  status: z.coerce.number().int().min(1).max(3).optional().catch(undefined),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

interface UseRecordingsReturn {
  recordings: Recording[];
  total: number;
  page: number;
  pageSize: number;
  filters: RecordingFilters;
  setPage: (page: number) => void;
  search: (filters: RecordingFilters) => void;
}

const useRecordings = (): UseRecordingsReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URLパラメータをオブジェクトに変換
  const rawParams = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);

  // zodでバリデーション
  const validatedParams = useMemo(() => urlParamsSchema.parse(rawParams), [rawParams]);

  // kebab-case → camelCaseの変換
  const page = validatedParams.page;
  const filters: RecordingFilters = useMemo(
    () => ({
      customerName: validatedParams['customer-name'],
      customerPhone: validatedParams['customer-phone'],
      advisorName: validatedParams['advisor-name'],
      recordingStatusId: validatedParams.status,
      dateFrom: validatedParams.from,
      dateTo: validatedParams.to,
    }),
    [validatedParams]
  );

  // URL正規化チェック
  const normalizedParams = useMemo(() => buildNormalizedParams(page, filters), [page, filters]);

  const currentUrl = searchParams.toString();
  const normalizedUrl = normalizedParams.toString();
  const needsNormalization = currentUrl !== normalizedUrl;

  // URLが不正だった場合、正規化されたURLに置き換える
  useEffect(() => {
    if (needsNormalization) {
      setSearchParams(normalizedParams, { replace: true });
    }
  }, [needsNormalization, normalizedParams, setSearchParams]);

  // APIパラメータを構築（JST→UTC変換を含む）
  const queryParams: RecordingsQueryParams = useMemo(() => {
    const params: RecordingsQueryParams = {
      page: String(page),
    };

    if (filters.customerName) {
      params.customerName = filters.customerName;
    }
    if (filters.customerPhone) {
      params.customerPhone = filters.customerPhone;
    }
    if (filters.advisorName) {
      params.advisorName = filters.advisorName;
    }
    if (filters.recordingStatusId) {
      params.recordingStatusId = String(filters.recordingStatusId);
    }
    if (filters.dateFrom) {
      params.dateFrom = convertJstDateToUtcIso(filters.dateFrom, false);
    }
    if (filters.dateTo) {
      params.dateTo = convertJstDateToUtcIso(filters.dateTo, true);
    }

    return params;
  }, [page, filters]);

  // パラメータatomを更新（atom側で重複チェックするため、常に呼び出してOK）
  const setParams = useSetAtom(recordingsQueryParamsAtom);

  useEffect(() => {
    setParams(queryParams);
  }, [queryParams, setParams]);

  // Suspenseと組み合わせてデータを取得（atomインスタンスは固定）
  const data = useAtomValue(recordingsAtom);

  // 最大ページ数を超えている場合は1ページ目にリダイレクト
  const maxPage = calculateMaxPage(data.count, PAGE_SIZE);
  useEffect(() => {
    if (page > maxPage) {
      const params = buildNormalizedParams(1, filters);
      setSearchParams(params, { replace: true });
    }
  }, [page, maxPage, filters, setSearchParams]);

  // ページ変更（検索条件は維持）
  const setPage = useCallback(
    (newPage: number) => {
      const params = buildNormalizedParams(newPage, filters);
      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  // 検索実行（ページを1にリセット）
  const search = useCallback(
    (newFilters: RecordingFilters) => {
      const params = buildNormalizedParams(1, newFilters);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  return {
    recordings: data.recordings,
    total: data.count,
    page,
    pageSize: PAGE_SIZE,
    filters,
    setPage,
    search,
  };
};

export default useRecordings;
